/**
 * The state of the app, as signals.
 *
 * This used to be a single `state` object, and after every change the matching
 * `renderX()` had to be called by hand. That worked as long as somebody kept
 * every call site in their head — and that is exactly where it failed in the
 * end: setting `state.errors` without calling `renderOverlay()` left old
 * squiggles on the paper, and nothing said a word.
 *
 * Signals turn it around: whoever reads, subscribes.
 *
 * CAREFUL with sets. `solved` used to be a `Set` mutated in place
 * (`solved.add(...)`). A signal does not see that — it compares the reference,
 * and that stays the same. Hence the immutable values here, and the helpers
 * that write a new set.
 */

import { signal, computed } from '@preact/signals'
import type { Doc, Finding, Session, Paper, PaperId, Font } from './types.js'
import { paperDef, DEFAULT_PAPER } from './ui/paper.js'
import type { LanguageToolChecker } from './spell/check.js'
import type { Solved } from './editor/marks.js'

// --- Prüfung ----------------------------------------------------------------

/** The checker itself. Not a signal: a service, not state. */
export let checker: LanguageToolChecker | null = null
export const setChecker = (c: LanguageToolChecker | null): void => {
  checker = c
}

export const checking = signal(true)

/**
 * Do comma mistakes count as mistakes?
 *
 * In a free text they are by far the most frequent finding — measured on a
 * school text, 18 of 23 marks. All of them in the same red as a misspelled
 * word makes the sheet look like a failure and drowns the two words that are
 * really written wrong.
 *
 * Switched off, the commas do not disappear: they get a quieter colour and
 * stay out of the count. The help behind them remains reachable.
 */
export const commas = signal(true)

/** The words marked as wrong. */
export const errors = signal<Finding[]>([])

/** The open help: word position, and how far the help has gone. */
export const session = signal<Session | null>(null)

/**
 * Words flashing green right now.
 *
 * What is stored is the whole RANGE, not just the start: a word put right is
 * no longer a finding, so it drops out of `errors`, and the green would have
 * nothing left to hang on. That is exactly why the confirmation used to stay
 * invisible.
 *
 * As an immutable map: `solved.value = new Map(...)` instead of `.set()`. A
 * `set()` on the same map would trigger no signal, and the green would stay
 * away again — without an error anywhere.
 */
export const solved = signal<ReadonlyMap<number, Solved>>(new Map())

export function markSolved(start: number, end: number): void {
  const next = new Map(solved.value)
  next.set(start, { start, end })
  solved.value = next
}

export function unmarkSolved(start: number): void {
  const next = new Map(solved.value)
  next.delete(start)
  solved.value = next
}

/** The personal dictionary (names and so on). */
export const personal = signal<string[]>([])

// --- Text und Datei ---------------------------------------------------------

/** The open text, or null for a fresh sheet. */
export const doc = signal<Doc | null>(null)

/** Are there unsaved changes? */
export const dirty = signal(false)

// --- Papier und Schrift -----------------------------------------------------

export const paper = signal<PaperId>(DEFAULT_PAPER)

/** Print the rules too? Usually not — exercise-book paper has its own. */
export const printBg = signal(false)

/** For squared paper: leave one row of squares free between the lines. */
export const gridSpacing = signal(false)

export const fonts = signal<Font[]>([])

/** Which font is in use right now ('builtin' or a font id). */
export const fontId = signal<string>('builtin')

// --- Menüs und Karten -------------------------------------------------------

/**
 * Which panel is open. One value instead of six booleans: two panels open at
 * once never happened, and as a single value it cannot happen.
 */
export type Panel = 'docs' | 'words' | 'check' | 'paper' | 'photo' | null
export const panel = signal<Panel>(null)

/** On the phone: are the controls behind the burger? */
export const burger = signal(false)

/**
 * Is an exercise running?
 *
 * Not a panel but a view of its own: it lies over the paper, and on the phone
 * it fills the screen. Whoever practises, practises — and does not write on
 * the side.
 */
export const exercise = signal(false)

/**
 * Which screen is showing.
 *
 * `start` is the way in — writing, photographing, practising, and the figures
 * on how it is going. `paper` is the writing itself.
 *
 * The app used to open straight onto the paper, with everything else behind
 * the burger menu. Whoever opened it could not see that seven words were
 * waiting; the exercises, which are the point of the whole thing, were the
 * hardest part to reach.
 *
 * `stats` is the detail: which kinds of mistake, and how they develop.
 *
 * `photo` explains what photographing is FOR before the camera opens, and
 * lists what has been photographed so far. Tapping the tile used to go
 * straight to the camera, and nothing said why one would point it anywhere.
 */
export type Screen = 'start' | 'paper' | 'stats' | 'photo'

export const screen = signal<Screen>('start')

/**
 * Goes to a screen, and remembers where one came from.
 *
 * Every change of screen goes through here, and that is the point: two things
 * have to happen with each one, and doing them by hand at ten call sites
 * means forgetting one.
 *
 * It CLOSES WHAT IS OPEN. A card hangs off the screen it was opened on — the
 * text list over the paper, the transcript over the photo screen. Navigating
 * away left it standing there, floating over a screen it has nothing to do
 * with.
 *
 * And it writes an entry into the browser's history, so the back button
 * leads back rather than out of the app. No address with it: the key lives
 * in the URL fragment, and a visible `/stats` would be one more way to hand
 * it to somebody by accident.
 *
 * @param push  false when the change ITSELF came from the history — going
 *              back must not add another entry to go back through.
 */
export function goTo(next: Screen, push = true): void {
  if (screen.value === next) return
  // Whatever was open belonged to the screen being left.
  panel.value = null
  burger.value = false
  session.value = null
  screen.value = next
  if (push) history.pushState({ screen: next }, '')
}

/**
 * Whose app this is.
 *
 * `null` means: not asked yet. That is a THIRD state, not a shade of "not
 * signed in" — without it the sign-in screen would flash up for a moment on
 * every start before the answer arrives, at the one moment when the child is
 * looking at the screen most closely.
 */
export const user = signal<{
  name: string
  /**
   * A parent looking at the child's Kritzl — see `acts_for` in the keys
   * table. Everything on screen is the child's; nothing done here counts
   * for the child. `viewer` is the parent's own name, for the greeting.
   */
  parent?: boolean
  viewer?: string
} | null>(null)

/** Is this a parent's view? Then no progress is booked anywhere. */
export const isParent = (): boolean => !!user.value?.parent

/** Has the key been checked at all? Only then may anything be shown. */
export const checkedKey = signal(false)

/**
 * Is this a device one writes on?
 *
 * The plan behind the app: the PC is for writing, the phone for the
 * exercises and for photographing a page. An on-screen keyboard corrects by
 * itself and would do exactly what is supposed to be practised.
 *
 * What decides is TOUCH, not the width of the screen. An iPad in landscape
 * is wide and still no place to write; a narrow window on the PC is.
 *
 * `pointer: coarse` and not `maxTouchPoints`: a laptop with a touchscreen
 * reports touch points and is a perfectly good machine to write on. What is
 * asked here is what the MAIN pointing device is.
 */
export const canWrite = signal(
  typeof matchMedia === 'function' ? !matchMedia('(pointer: coarse)').matches : true
)

/** How many issues are waiting to be practised. */
export const openIssues = signal(0)

/**
 * Is the model reading a written text right now?
 *
 * Set while the start screen has a text read — see `reviewWritten` in
 * App.tsx. It shows as a strip at the bottom edge and blocks nothing: the
 * reading runs whether or not anybody watches it.
 */
export const reviewing = signal(false)

export const toast = signal<string>('')

/** Is the warning in the check menu showing that checking failed to start? */
export const checkWarning = signal(false)

// --- Abgeleitetes -----------------------------------------------------------

/**
 * How many findings count.
 *
 * Comma findings stay out when they are switched off — they then stand
 * quieter on the paper, but not in the number.
 */
export const errorCount = computed(
  () => errors.value.filter((f) => commas.value || !isCommaRule(f.rule)).length
)

/** Comma rules, recognised roughly by name. The fine check lives in the editor. */
const isCommaRule = (rule: string): boolean =>
  rule.startsWith('KOMMA') || rule.includes('COMMA') || rule.includes('KOMMASETZUNG')

/** The title of the open text, for the bar. */
export const title = computed(() => doc.value?.title ?? '')

/**
 * The paper currently chosen.
 *
 * `PAPERS` is a constant, not a signal — the kinds of sheet are fixed, only
 * the choice changes.
 */
export const currentPaper = computed<Paper>(() => paperDef(paper.value))
